import { jsx } from 'panel';
import { hasSlot } from 'common/util/dom';
import { ButtonEl } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { CheckboxEl } from 'common/components/checkbox';
import { SvgIconEl } from 'common/components/svg-icon';
import { TooltipEl } from 'common/components/tooltip';
import { Placement } from 'common/components/tooltip/types';
import { FieldWithMatches } from './cell-mixins';
/** Built in default mixins **/

const ColumnTitle = _ref => {
  let {
    column,
    colIndex,
    columnTitle
  } = _ref;
  return column.titleRenderer ? column.titleRenderer(column, {
    colIndex,
    title: columnTitle
  }) : jsx("div", {
    sel: '.text'
  }, columnTitle);
};

const CellTypeText = _ref2 => {
  let {
    item,
    context
  } = _ref2;
  return jsx(FieldWithMatches, {
    item: item,
    context: context
  });
};

const CellTypeCollapsible = _ref3 => {
  let {
    item,
    component
  } = _ref3;
  return jsx("div", {
    sel: '.mp-table-collapsible'
  }, jsx(ButtonEl, {
    attrs: {
      icon: item.isExpanded ? "caret-down" : "caret-right",
      theme: ButtonTheme.Tertiary
    },
    on: {
      click: ev => component.helpers.handleCollapsibleCaretClick(ev, item)
    }
  }));
};

const CellTypeCheckbox = _ref4 => {
  let {
    item,
    context,
    component
  } = _ref4;
  const options = component.helpers.getCheckboxState(item);
  return jsx("div", {
    sel: '.mp-table-checkbox-wrapper',
    class: {
      disabled: options.attrs.disabled,
      'will-range-select': component.helpers.willRangeSelect(context.rowIdx)
    },
    on: {
      click: ev => !options.attrs.disabled && component.helpers.toggleSelected(ev, item, context.rowIdx)
    },
    attrs: {
      title: component.helpers.rangeSelectAllowed() ? "shift + click for range select" : ""
    }
  }, jsx(CheckboxEl, {
    attrs: options.attrs
  }), options.selectDisabledReason && options.attrs.disabled ? jsx(TooltipEl, {
    attrs: {
      placement: Placement.Right
    }
  }, options.selectDisabledReason) : null);
};
/** End Section */


export function template() {
  const {
    draggingColumnIndex,
    hasBodyScrolled,
    rowDetailsRenderer,
    sortField,
    sortOrder
  } = this.state;
  const draggableColumns = this.attr("draggable-columns");
  const rowsToDisplay = this.helpers.getRowsToDisplay();
  const colsToDisplay = this.helpers.getColumnsToDisplay();
  const isGroupingEnabled = this.helpers.isGroupingEnabled();
  return jsx("div", {
    sel: '.mp-table',
    on: {
      scroll: !this.attr("window-scroll") && this.helpers.handleTableScroll
    }
  }, (() => {
    const isSticky = this.attr("sticky-titles") && !this.attr("loading");
    return jsx(null, null, !this.attr("hide-titles") ? jsx("div", {
      sel: '.mp-table-titles',
      class: {
        'mp-table-titles-sticky': isSticky
      },
      hook: isSticky && !this.attr("disable-page-sticky-titles") ? this.helpers.stickyElemHooks : {}
    }, colsToDisplay.map((column, colIndex) => {
      const isCurrSortField = sortField === column.field;
      const isDraggable = draggableColumns && column.field !== "isSelected" && this.helpers.isColumnDraggable(colIndex);
      return jsx("div", {
        sel: '.mp-table-title-cell',
        class: {
          'mp-table-can-sort': column.isSortable,
          'mp-table-is-draggable': isDraggable,
          'mp-table-sort-column': isCurrSortField,
          ["mp-table-title-cell-type-".concat(column.type)]: !!column.type,
          ["title-cell-field-".concat(column.field)]: true,
          'drop-indicator-left': draggableColumns && this.helpers.isDroppingLeft(colIndex),
          'drop-indicator-right': draggableColumns && this.helpers.isDroppingRight(colIndex)
        },
        style: {
          flexGrow: column.flexGrow || "0",
          flexBasis: column.flexBasis
        },
        on: {
          click: () => this.helpers.handleHeaderTitleClick(column),
          dragenter: () => this.helpers.handleTitleDragEnter(colIndex),
          dragover: ev => this.helpers.handleTitleDragOver(ev, colIndex),
          dragleave: this.helpers.handleTitleStepDragLeave,
          dragend: this.helpers.handleTitleStepDragEnd,
          drop: ev => this.helpers.handleTitleDrop(ev, colIndex),
          mousedown: ev => this.helpers.handleTitleMouseDown(ev, colIndex),
          mouseup: this.helpers.handleTitleMouseUp
        }
      }, column.type === "collapsible" ? jsx("div", {
        sel: '.mp-table-collapsible'
      }, jsx(ButtonEl, {
        attrs: {
          icon: "collapse",
          theme: ButtonTheme.Tertiary
        },
        on: {
          click: () => this.helpers.handleCollapseAll()
        }
      })) : null, column.type === "checkbox" ? // checkbox-wrapper so we can increase click surface area
      jsx("div", {
        sel: '.mp-table-checkbox-wrapper',
        style: {
          pointerEvents: this.attr("multi-select") ? "" : "none"
        },
        on: {
          click: this.helpers.toggleAllSelected
        }
      }, this.attr("multi-select") ? (() => {
        const {
          numSelected,
          allSelected
        } = this.helpers.getSelectionStats();
        return jsx(CheckboxEl, {
          attrs: {
            checked: allSelected,
            'unchecked-icon': numSelected > 0 ? "dash" : ""
          }
        });
      })() : null) : null, isDraggable ? jsx("div", {
        sel: '.mp-table-drag-controls',
        attrs: {
          draggable: String(isDraggable)
        },
        on: {
          dragstart: ev => this.helpers.handleTitleDragStart(ev, colIndex)
        }
      }, jsx(SvgIconEl, {
        attrs: {
          icon: "gripper"
        }
      })) : null, column.title ? jsx("div", {
        sel: '.mp-table-title-label'
      }, (() => {
        const maxLength = column.titleMaxLength || this.helpers.COLUMN_TITLE_MAX_LENGTH;
        const columnTitle = column.title.length > maxLength ? this.helpers.truncateMiddle(column.title, maxLength) : column.title;
        return jsx(null, null, jsx(ColumnTitle, {
          column: column,
          colIndex: colIndex,
          columnTitle: columnTitle
        }), column.title.length > maxLength && colIndex !== draggingColumnIndex ? jsx(TooltipEl, {
          attrs: {
            'max-width': "250px",
            placement: Placement.Top
          }
        }, column.title) : null);
      })()) : null, column.titleIcon ? jsx("div", {
        sel: '.mp-table-title-icon'
      }, jsx(SvgIconEl, {
        attrs: {
          icon: column.titleIcon
        }
      }), column.titleTooltip ? jsx(TooltipEl, {
        attrs: {
          'max-width': "250px",
          placement: Placement.Top
        }
      }, column.titleTooltip) : null) : null, column.helpText ? jsx("div", {
        sel: '.mp-table-title-help-text'
      }, (() => {
        const hrefVal = column.helpLink ? column.helpLink : "javascript:void(0)";
        return jsx(null, null, column.helpLink ? jsx("a", {
          attrs: {
            href: hrefVal,
            target: "_blank"
          },
          class: {
            'mp-table-title-help-link': !!column.helpLink
          }
        }, jsx(SvgIconEl, {
          attrs: {
            icon: "info"
          }
        })) : jsx(SvgIconEl, {
          attrs: {
            icon: "info"
          }
        }), jsx(TooltipEl, {
          attrs: {
            'max-width': "250px",
            placement: column.helpTextPlacement
          }
        }, column.helpText));
      })()) : null, column.isSortable ? jsx("div", {
        sel: '.mp-table-sort-controls',
        class: {
          ["mp-table-sort-".concat(sortOrder === 1 ? "asc" : "desc")]: isCurrSortField
        }
      }, jsx("div", {
        sel: '.mp-table-sort-caret-up'
      }, jsx(SvgIconEl, {
        attrs: {
          icon: "caret-up"
        }
      })), jsx("div", {
        sel: '.mp-table-sort-caret-down'
      }, jsx(SvgIconEl, {
        attrs: {
          icon: "caret-down"
        }
      }))) : null);
    })) : null, jsx("div", {
      sel: '.mp-table-body',
      class: {
        'mp-table-body-clickable': !this.attr("disable-clickable-rows") && !this.attr("loading"),
        'mp-table-body-scroll': !this.attr("window-scroll"),
        'mp-table-body-scrolled': hasBodyScrolled
      },
      style: {
        maxHeight: this.attr("max-body-height")
      },
      on: {
        mouseleave: this.helpers.handleBodyMouseLeave,
        mouseenter: this.helpers.handleBodyMouseEnter,
        scroll: !this.attr("window-scroll") && this.helpers.handleScroll
      },
      hook: {
        insert: _ref5 => {
          let {
            elm
          } = _ref5;
          return this.helpers.updateElRef({
            tableBody: elm
          });
        }
      }
    }, this.attr("loading") ? jsx("div", {
      sel: '.mp-table-row.mp-table-row-loading'
    }, [...Array(5)].map(() => jsx("div", {
      sel: '.mp-skeleton-large'
    }))) : rowsToDisplay.length ? (() => {
      const needsRowMouseEnter = this.attr("row-focused-context") || this.attr("multi-select");
      let prevRowContexts = null;
      return jsx(null, null, rowsToDisplay.map((row, rowIdx) => jsx(null, null, jsx("div", {
        sel: '.mp-table-row.mp-table-body-row',
        class: {
          ["mp-table-row-no-border"]: isGroupingEnabled,
          ["mp-table-row-selected"]: row.val.isSelected,
          ["mp-table-row-no-bottom-border"]: row.val.isExpanded
        },
        on: {
          click: ev => this.helpers.handleRowClick(ev, row.val, rowIdx),
          mouseenter: needsRowMouseEnter && (() => this.helpers.handleRowMouseEnter(rowIdx))
        }
      }, (() => {
        const currRowContexts = this.helpers.getCellRenderContexts(colsToDisplay, row, rowIdx, rowsToDisplay.length);
        const groupedColIndices = isGroupingEnabled && this.helpers.getGroupedColIndices(colsToDisplay, prevRowContexts, currRowContexts);
        prevRowContexts = currRowContexts;
        return jsx(null, null, row.val.isNew ? jsx("div", {
          sel: '.mp-table-new-row-indicator'
        }) : null, colsToDisplay.map((column, columnIdx) => {
          const columnType = this.helpers.getColumnType(row.val, column);
          const context = currRowContexts[columnIdx];
          const cellRenderer = column.cellRenderer;
          let CellRendererMixin;

          if (!cellRenderer) {
            switch (columnType) {
              case "checkbox":
                CellRendererMixin = CellTypeCheckbox;
                break;

              case "collapsible":
                CellRendererMixin = CellTypeCollapsible;
                break;

              default:
                CellRendererMixin = CellTypeText;
                break;
            }
          }

          return jsx("div", {
            sel: '.mp-table-cell',
            key: column.field,
            class: {
              ["cell-field-".concat(column.field)]: true,
              ["mp-table-cell-has-right-border"]: isGroupingEnabled && column.isGroupable,
              ["mp-table-cell-has-top-border"]: isGroupingEnabled && rowIdx > 0 && !groupedColIndices.has(columnIdx),
              ["mp-table-cell-sortable"]: column.isSortable,
              ["mp-table-cell-type-".concat(columnType)]: !!columnType,
              'drop-indicator-left': this.helpers.isDroppingLeft(columnIdx),
              'drop-indicator-right': this.helpers.isDroppingRight(columnIdx)
            },
            style: {
              flexGrow: column.flexGrow || "0",
              flexBasis: column.flexBasis
            },
            on: {
              click: ev => this.helpers.handleCellClick(ev, row.val, column.field, context)
            }
          }, !(isGroupingEnabled && groupedColIndices.has(columnIdx)) ? cellRenderer ? cellRenderer(row.val, context) : CellRendererMixin ? jsx(CellRendererMixin, {
            item: row.val,
            context: context,
            component: this
          }) : jsx(CellTypeText, {
            item: row.val,
            context: context
          }) : null);
        }));
      })()), rowDetailsRenderer && row.val.isExpanded ? jsx("div", {
        sel: '.mp-table-row-details'
      }, rowDetailsRenderer(row.val)) : null)), Array.from({
        length: this.attr("min-row-count") - rowsToDisplay.length
      }).map(_ => jsx("div", {
        sel: '.mp-table-row.mp-table-row-no-border.mp-table-padding-row'
      })));
    })() : jsx("div", {
      sel: '.mp-table-row.mp-table-row-empty'
    }, this.attr("search-filter").trim() ? "Your search returned no results" : hasSlot(this, "table-empty-message") ? jsx("slot", {
      attrs: {
        name: "table-empty-message"
      }
    }) : this.attr("empty-message"))));
  })());
}